import { INDIA_CITIES, PAKISTAN_CITIES, type Country } from "./gold-data";

/**
 * Typo-tolerant URL normalizer.
 * Maps misspelled / phonetic search strings (Hindi-English, Urdu-English)
 * into one of:
 *   - /pakistan or /india  (country hub)
 *   - /city/<country>/<city-slug>  (specific city)
 *   - /tools/...  (tool routes)
 *   - /  (fallback for ambiguous gold queries)
 */

const COUNTRY_WORDS: Record<string, Country> = {
  pakistan: "pakistan", pak: "pakistan", pk: "pakistan", paki: "pakistan", pakstan: "pakistan",
  india: "india", ind: "india", in: "india", bharat: "india", hindustan: "india", indya: "india", inida: "india",
};

// City aliases — common phonetic/typo variants → canonical slug
const CITY_ALIASES: Record<string, { country: Country; slug: string }> = {
  // Pakistan
  karachi: { country: "pakistan", slug: "karachi" }, karachee: { country: "pakistan", slug: "karachi" }, krachi: { country: "pakistan", slug: "karachi" }, karchi: { country: "pakistan", slug: "karachi" },
  lahore: { country: "pakistan", slug: "lahore" }, lahor: { country: "pakistan", slug: "lahore" }, lhr: { country: "pakistan", slug: "lahore" }, lahour: { country: "pakistan", slug: "lahore" },
  islamabad: { country: "pakistan", slug: "islamabad" }, isb: { country: "pakistan", slug: "islamabad" }, islmbd: { country: "pakistan", slug: "islamabad" }, islamabd: { country: "pakistan", slug: "islamabad" },
  rawalpindi: { country: "pakistan", slug: "rawalpindi" }, pindi: { country: "pakistan", slug: "rawalpindi" }, rwp: { country: "pakistan", slug: "rawalpindi" },
  peshawar: { country: "pakistan", slug: "peshawar" }, pshwr: { country: "pakistan", slug: "peshawar" }, peshwar: { country: "pakistan", slug: "peshawar" },
  quetta: { country: "pakistan", slug: "quetta" }, queta: { country: "pakistan", slug: "quetta" },
  multan: { country: "pakistan", slug: "multan" }, mltn: { country: "pakistan", slug: "multan" },
  faisalabad: { country: "pakistan", slug: "faisalabad" }, fsd: { country: "pakistan", slug: "faisalabad" }, faislabad: { country: "pakistan", slug: "faisalabad" },
  sialkot: { country: "pakistan", slug: "sialkot" }, skt: { country: "pakistan", slug: "sialkot" },
  gujranwala: { country: "pakistan", slug: "gujranwala" }, gujrnwala: { country: "pakistan", slug: "gujranwala" }, grw: { country: "pakistan", slug: "gujranwala" },

  // India
  delhi: { country: "india", slug: "delhi" }, dehly: { country: "india", slug: "delhi" }, dehlly: { country: "india", slug: "delhi" }, dilli: { country: "india", slug: "delhi" }, dehli: { country: "india", slug: "delhi" }, dehlhi: { country: "india", slug: "delhi" }, newdelhi: { country: "india", slug: "delhi" },
  mumbai: { country: "india", slug: "mumbai" }, bombay: { country: "india", slug: "mumbai" }, mumbi: { country: "india", slug: "mumbai" }, mombai: { country: "india", slug: "mumbai" },
  chennai: { country: "india", slug: "chennai" }, madras: { country: "india", slug: "chennai" }, chenai: { country: "india", slug: "chennai" },
  kolkata: { country: "india", slug: "kolkata" }, calcutta: { country: "india", slug: "kolkata" }, kolkatta: { country: "india", slug: "kolkata" },
  bangalore: { country: "india", slug: "bangalore" }, bengaluru: { country: "india", slug: "bangalore" }, banglore: { country: "india", slug: "bangalore" },
  ahmedabad: { country: "india", slug: "ahmedabad" }, amdavad: { country: "india", slug: "ahmedabad" }, ahmdabad: { country: "india", slug: "ahmedabad" },
  pune: { country: "india", slug: "pune" }, poona: { country: "india", slug: "pune" },
  jaipur: { country: "india", slug: "jaipur" }, jajpur: { country: "india", slug: "jaipur" },
  lucknow: { country: "india", slug: "lucknow" }, luknow: { country: "india", slug: "lucknow" },
};

const TOOL_HINTS: Record<string, string> = {
  converter: "/tools/gold-purity-converter",
  purity: "/tools/gold-purity-converter",
  jewellery: "/tools/jewelry-making-estimator",
  jewelry: "/tools/jewelry-making-estimator",
  making: "/tools/jewelry-making-estimator",
  roi: "/tools/historical-roi-calculator",
  investment: "/tools/historical-roi-calculator",
  historical: "/tools/historical-roi-calculator",
  calculator: "/tools",
};

function tokens(input: string): string[] {
  return input
    .toLowerCase()
    .replace(/[^a-z0-9\s/-]+/g, " ")
    .replace(/[/_-]+/g, " ")
    .split(/\s+/)
    .filter(Boolean);
}

function levenshtein(a: string, b: string): number {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  const m = Array.from({ length: a.length + 1 }, () => new Array(b.length + 1).fill(0));
  for (let i = 0; i <= a.length; i++) m[i][0] = i;
  for (let j = 0; j <= b.length; j++) m[0][j] = j;
  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      m[i][j] = Math.min(m[i - 1][j] + 1, m[i][j - 1] + 1, m[i - 1][j - 1] + cost);
    }
  }
  return m[a.length][b.length];
}

function fuzzyCity(tok: string): { country: Country; slug: string } | null {
  if (tok.length < 4) return null;
  const all = [
    ...PAKISTAN_CITIES.map((c) => ({ country: "pakistan" as Country, slug: c.slug, name: c.name.toLowerCase() })),
    ...INDIA_CITIES.map((c) => ({ country: "india" as Country, slug: c.slug, name: c.name.toLowerCase() })),
  ];
  let best: { country: Country; slug: string; d: number } | null = null;
  for (const c of all) {
    const d = Math.min(levenshtein(tok, c.name), levenshtein(tok, c.slug));
    if (!best || d < best.d) best = { country: c.country, slug: c.slug, d };
  }
  if (best && best.d <= 2) return { country: best.country, slug: best.slug };
  return null;
}

/**
 * Topic gate for the catch-all normalizer.
 *
 * Historically ANY unmatched URL was fuzzy-matched to the nearest city and
 * 301'd into a gold page. That meant unrelated legacy paths left over from
 * the domain's previous owner (gaming/how-to junk such as
 * `/jailbreak-for-xbox-one/`) were redirected into commercial gold pages —
 * which reads as a doorway/soft-404 pattern rather than a helpful
 * correction. A typo redirect now only fires when the URL is genuinely
 * about a precious-metal rate; everything else gets an honest 404.
 */
const TOPIC_TOKENS = new Set([
  "gold", "gld", "sona", "sone", "sonna", "swarn", "swarna", "tola", "tolla",
  "karat", "carat", "k", "22k", "24k", "21k", "18k", "bullion", "sarafa",
  "silver", "chandi", "chandhi", "rate", "rates", "rat", "price", "prices",
  "bhav", "bhaav", "qeemat", "qimat", "keemat", "value", "today", "aaj", "aj",
  "gram", "grams", "ratti", "sovereign", "ounce", "tola-rate", "jewellery",
  "jewelry", "zewar", "zakat", "nisab", "hallmark", "purity",
]);

function isMetalTopic(toks: string[]): boolean {
  return toks.some((t) => TOPIC_TOKENS.has(t));
}

export function normalizeTypoPath(rawPath: string): string {
  const toks = tokens(rawPath);

  // Tool intent first
  for (const t of toks) {
    if (TOOL_HINTS[t]) return TOOL_HINTS[t];
  }

  // Everything below rewrites the visitor into a gold/silver page, so it may
  // only run when the URL itself is about a metal rate.
  if (!isMetalTopic(toks)) return "";

  // Direct city alias
  for (const t of toks) {
    const c = CITY_ALIASES[t];
    if (c) return `/city/${c.country}/${c.slug}`;
  }

  // Fuzzy city match
  for (const t of toks) {
    const c = fuzzyCity(t);
    if (c) return `/city/${c.country}/${c.slug}`;
  }

  // Country hub
  for (const t of toks) {
    if (COUNTRY_WORDS[t]) return `/${COUNTRY_WORDS[t]}`;
  }

  // Unknown legacy / spam paths should render the lightweight noindex fallback
  // instead of redirecting every bot to the homepage and inflating root LCP data.
  return "";
}

// Common search-typo phrases that we want explicitly indexed in sitemap.xml
// so they survive across crawls. Each maps to a real route via the catch-all.
export const TYPO_SEED_PATHS: string[] = [
  "/gold-rate-lahor",
  "/gold-rate-mumbi",
  "/gold-rate-bombay",
  "/gold-rate-calcutta",
  "/gold-rate-bengaluru",
  "/gold-rate-pindi",
  "/22k-gold-prize-today",
  "/24k-gold-rat-pakistan",
  "/gold-converter",
  "/jewellery-making-charges",
];